// includes/js-src/janus-engine/pl/cost.ts — TS twins of the PHP cost lookups the PL pricer calls (Janus C2):
// PlCost::* (includes/catalogs/PlCost.php), SpellCost / SkillTier / DamageCost (includes/additions.php),
// ft_pl_size_gated_defense_gp (size_gated_defenses.php). Every table comes from pl-cost-contract.json; the LOGIC of each
// helper is ported line for line, including the (int) casts and round() calls the PHP performs, so the same inputs price
// the same gp. Proven by tools/test/janus-pl-contract.test.mjs against tools/test/fixtures/pl-cost-selftest.json.
// Plan: plans/janus-c2-pl-in-browser-plan-2026-09-16.md §6.1, §7 step 0.
import { phpRound, phpInt, phpIntRound } from './php-round';
import { plCostContract, plCatalogs, type PlCostContract } from './contract';
import { phpJsonTruthy, phpJsonInt } from '../compile/php-json-values';
import { readItemNumericSlot } from '../compile/enchantment-template';
import { isPhpNumeric, phpJsonFloat } from '../compile/php-decimal-values';
import type { ItemFieldRow } from '../compile/item-field-reader';
import { plCostLayerEntryUuid } from './layer-cells';

/** layer_value() twin the array-reading helpers take — the corpus reader bound to a definition resolver. */
export type LayerValueReader = (row: ItemFieldRow | null, name: string) => unknown;
export type LayerHasReader = (row: ItemFieldRow | null, name: string) => boolean;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown): number => typeof v === 'number' ? v : Number(v ?? 0);
/** PHP `$table[$key] ?? …` — an OWN key only (an inherited name such as `constructor` is absent in PHP). */
const cell = (t: Record<string, unknown>, key: string): unknown => Object.prototype.hasOwnProperty.call(t, key) ? t[key] : undefined;
/** PHP `int` return-type coercion of a table cell (coercive mode): an int passes, a numeric value is truncated, a table
 * (array) or a non-numeric string is a TypeError in PHP. Tables come from PHP constants, so this is a tripwire, not a path. */
function tableInt(v: unknown, where: string): number {
    if (v === undefined || v === null) return 0;
    if (typeof v === 'number') return Number.isInteger(v) ? v : phpInt(v);
    if (typeof v === 'string' && isPhpNumeric(v)) return phpInt(phpJsonFloat(v));
    throw new TypeError(`PlCost::${where}: table value is not an int (PHP would fatal here)`);
}

export class PlCost {
    readonly COSTS: PlCostContract['plCosts'];
    constructor(readonly contract: PlCostContract = plCostContract) { this.COSTS = contract.plCosts; }
    private table(category: string): Record<string, unknown> { const t = cell(this.COSTS as Record<string, unknown>, category); return isObj(t) ? t : {}; }
    private sub(category: string, group: string): Record<string, unknown> { const t = cell(this.table(category), group); return isObj(t) ? t : {}; }

    getCost(category: string, variableName: string, mode = 'stackable'): number {
        if (category === 'special') return tableInt(cell(this.table('special'), variableName), 'getCost');
        return tableInt(cell(this.sub(category, mode), variableName), 'getCost');
    }
    getDivisor(category: string): number { const d = cell(this.table(category), 'divisor'); return d === undefined || d === null ? 1 : tableInt(d, 'getDivisor'); }
    getStatusEffectModifierCost(layerType: string, statusVarName: string): number {
        return tableInt(cell(this.sub('status_effects', layerType), statusVarName), 'getStatusEffectModifierCost');
    }
    getSkillRollModifierCost(layerType: string, skillVarName = ''): number {
        const layer = cell(this.table('skill_roll_modifiers'), layerType);
        if (skillVarName === '') return tableInt(layer, 'getSkillRollModifierCost');
        if (isObj(layer) && Object.prototype.hasOwnProperty.call(layer, skillVarName)) return tableInt(cell(layer, skillVarName), 'getSkillRollModifierCost');
        if (layerType === 'skill_is_rolled_at_min') return tableInt(cell(this.sub('skill_roll_modifiers', 'skill_roll_dis'), skillVarName), 'getSkillRollModifierCost');
        if (layerType === 'skill_is_rolled_at_max') return tableInt(cell(this.sub('skill_roll_modifiers', 'skill_roll_adv'), skillVarName), 'getSkillRollModifierCost');
        return 0;
    }
    getRollTypeModifierCost(layerType: string, rollVarName = ''): number {
        if (rollVarName === '') return 0;
        return tableInt(cell(this.sub('roll_type_modifiers', layerType), rollVarName), 'getRollTypeModifierCost');
    }
    getDamageModifierCost(layerType: string, damageVarName = '', incrementValue = 0): number {
        if (incrementValue > 0) {
            const baseCost = tableInt(cell(this.table('damage_modifiers'), layerType), 'getDamageModifierCost');
            let gp = baseCost * (incrementValue / 5);
            if (layerType === 'negate_falling_damage_in_feet') gp = Math.min(gp, 10000);
            return phpInt(gp);
        }
        if (damageVarName === '') return tableInt(cell(this.table('damage_modifiers'), layerType), 'getDamageModifierCost');
        return tableInt(cell(this.sub('damage_modifiers', layerType), damageVarName), 'getDamageModifierCost');
    }
    getApModifierCost(layerType: string): number { return tableInt(cell(this.table('ap_modifiers'), layerType), 'getApModifierCost'); }
    getForceMovementModifierCost(layerType: string, incrementValue = 0): number {
        const base = tableInt(cell(this.table('force_movement_modifiers'), layerType), 'getForceMovementModifierCost');
        if (layerType === 'pull_ft_after_hit' || layerType === 'push_ft_after_hit') return phpInt(base * (incrementValue / 5));
        return base;
    }
    getLightModifierCost(layerType: string, value = 0): number {
        const base = tableInt(cell(this.table('light_modifiers'), layerType), 'getLightModifierCost');
        if (layerType === 'bright_light_dim_light' || layerType === 'limited_visibility_ft') return phpIntRound((value / 5) * base);
        return base;
    }
    getMiscModifierCost(layerType: string, value: number = 0): number {
        if (layerType === 'ac_mod_magical' || layerType === 'ac_mod_non_magical') {
            const v = Math.abs(value);
            const perOne = phpInt(num(cell(this.sub('misc_modifiers', 'per_one'), layerType) ?? 1000));
            return phpInt(v * perOne);
        }
        if (layerType === 'ac_mod') return phpInt(Math.abs(value) * 800);
        if (layerType === 'v_dice_multiplier') {
            const mult = Number(value);
            if (mult <= 1.0) return 0;
            const perQuarter = phpInt(num(cell(this.sub('misc_modifiers', 'per_one'), 'v_dice_multiplier') ?? 2500));
            const quarters = phpIntRound((mult - 1.0) / 0.25);
            return Math.max(0, quarters) * perQuarter;
        }
        const flat = this.sub('misc_modifiers', 'flat');
        if (cell(flat, layerType) !== undefined && cell(flat, layerType) !== null) return tableInt(cell(flat, layerType), 'getMiscModifierCost');
        const perOne = this.sub('misc_modifiers', 'per_one');
        if (cell(perOne, layerType) !== undefined && cell(perOne, layerType) !== null) {
            let costPer = tableInt(cell(perOne, layerType), 'getMiscModifierCost');
            if (layerType === 'change_creature_size' && value < 0) costPer = 2500;
            return phpInt(value * costPer);
        }
        const perFive = this.sub('misc_modifiers', 'per_five');
        if (cell(perFive, layerType) !== undefined && cell(perFive, layerType) !== null) {
            const costPerFive = tableInt(cell(perFive, layerType), 'getMiscModifierCost');
            let v = value;
            if (layerType === 'reach') v = Math.min(Math.max(0, v - 5), 30);
            return phpIntRound((v / 5) * costPerFive);
        }
        return 0;
    }
    getStatusApplicationCost(layerType: string, statusVarName: string | null, isCasterSelf = false): number {
        return this.getStatusApplicationCostDetailed(layerType, statusVarName, isCasterSelf).gp;
    }
    getStatusApplicationCostDetailed(layerType: string, statusVarName: string | null, isCasterSelf = false): { gp: number; details: Record<string, number> } {
        const baseImmune = statusVarName === null ? 0 : tableInt(cell(this.sub('status_effects', 'immune_to_status_effect'), statusVarName), 'getStatusApplicationCostDetailed');
        if (baseImmune === 0) return { gp: 0, details: {} };
        const base = phpIntRound(Math.abs(baseImmune) * 0.40);
        const reliability = (layerType.includes('_on_hit') || layerType.includes('_after_cast')) ? 1.00
            : layerType.includes('_after_contesting_roll_failed') ? 0.55
            : layerType.includes('_when_contestee_wins') ? 0.45
            : 1.00;
        let multiplier = reliability;
        const details: Record<string, number> = { immunity_gp: Math.abs(baseImmune), application_rate: 0.40, reliability };
        if (isCasterSelf) { multiplier *= 0.75; details.self_discount = 0.75; }
        if (layerType.includes('_removed_from_')) multiplier *= 1.0;
        return { gp: phpIntRound(base * multiplier), details };
    }
    getTriggerMultiplier(layerType: string): number {
        const m = cell(this.contract.triggerMultipliers as Record<string, unknown>, layerType);
        return typeof m === 'number' ? m : this.contract.triggerDefault;
    }
    /** PlCost::getDurationMultiplier — reads the arr through the bound layer_value twin. */
    getDurationMultiplier(arr: ItemFieldRow, read: LayerValueReader): number {
        for (const flag of ['effect_ends_on_target_start_of_turn', 'effect_ends_on_target_end_of_turn', 'effect_ends_on_start_of_caster_next_turn', 'effect_ends_on_end_of_caster_next_turn']) {
            if (phpJsonTruthy(read(arr, flag))) return 0.15;
        }
        const int = phpJsonInt(read(arr, 'duration_int') ?? 0);
        const typeRaw = read(arr, 'duration_type') ?? '';
        const type = typeof typeRaw === 'string' ? typeRaw : String(typeRaw);
        if (int === 0 && typeRaw === '') return 1.0;
        switch (type) {
            case 'seconds': return 0.20;
            case 'rounds': return Math.min(0.35, 0.15 + (int * 0.05));
            case 'minutes': return 0.30;
            case 'hours': return 0.45;
            case 'days': return 0.65;
            case 'weeks': case 'months': case 'years': return 0.80;
            case 'permanent': default: return 1.0;
        }
    }
    /** PlCost::getCreatureCost — speed_types always stackable; essences/senses fixed when a granted layer names the stat. */
    getCreatureCost(category: string, variableName: string, itemArr: ItemFieldRow | null, has: LayerHasReader): number {
        if (category === 'speed_types') return tableInt(cell(this.sub('speed_types', 'stackable'), variableName), 'getCreatureCost');
        let isBaseStat = true;
        for (const layer of ['ability_' + variableName, 'feat_' + variableName, 'inventory_' + variableName]) {
            if (has(itemArr, layer)) { isBaseStat = false; break; }
        }
        return tableInt(cell(this.sub(category, isBaseStat ? 'stackable' : 'fixed'), variableName), 'getCreatureCost');
    }
    getDamageMitigationCost(points: number): number { return points <= 0 ? 0 : points * 7500; }
    getBaseAcModCost(points: number): number { return points <= 0 ? 0 : points * 800; }
    /** The misc field lists the two pricers iterate (array_keys of per_one, per_five, flat). */
    miscFieldKeys(group: 'per_one' | 'per_five' | 'flat' | 'per_entry'): string[] { return Object.keys(this.sub('misc_modifiers', group)); }
}

export class SpellCost {
    constructor(readonly contract: PlCostContract = plCostContract) {}
    getCostForLevel(level: number): number { const v = this.contract.spellCostLevels[level]; return typeof v === 'number' ? v : 0; }
}

export class SkillTier {
    constructor(readonly contract: PlCostContract = plCostContract) {}
    getGpCost(variableName: string): number {
        for (const tier of this.contract.skillTiers) if (tier.variable_name === variableName) return tier.pl_gp_value ?? 0;
        return 0;
    }
    getRequirementDiscount(key: string): number {
        if (!key || !key.startsWith('requires_skill_tier_')) return 1.0;
        const m = /requires_skill_tier_(\d+)/.exec(key);
        if (!m) return 1.0;
        const level = Number.parseInt(m[1]!, 10);
        return level <= 1 ? 1.00 : level <= 2 ? 0.95 : level <= 3 ? 0.90 : level <= 4 ? 0.85 : level <= 5 ? 0.80 : level <= 6 ? 0.75 : level <= 7 ? 0.70 : level <= 8 ? 0.65 : 0.60;
    }
    /** SkillTier::getRequirementDiscountFromArray — the FIRST present key in LIST order wins (tier 0 first). */
    getRequirementDiscountFromArray(arr: ItemFieldRow | null, read: LayerValueReader): number {
        for (const key of this.contract.requiresSkillTierKeys) if (phpJsonTruthy(read(arr, key))) return this.getRequirementDiscount(key);
        return 1.0;
    }
}

/** ft_pl_size_gated_defense_gp twin. */
export function sizeGatedDefenseGp(layerName: string, n: number, costs: PlCost): number {
    const full = layerName === 'immune_to_forced_movement_from_size_category_number_below' ? costs.getDamageModifierCost('immune_to_forced_movement')
        : layerName === 'immune_to_status_effect_prone_from_size_category_number_below' ? costs.getStatusEffectModifierCost('immune_to_status_effect', 'prone')
        : 0;
    const share = Math.max(0.2, Math.min(1.0, 0.6 + 0.1 * n));
    return phpIntRound(full * share);
}

/** sprintf('%+d', n) */
const signed = (n: number): string => (n >= 0 ? '+' : '') + String(n);

export interface DamageGpDetails { gp: number; details: Record<string, unknown> }
/** DamageCost::getGpDetailed twin. `read` is the bound layer_value; the numeric slots go through readItemNumericSlot. */
export function damageCostGpDetailed(arr: ItemFieldRow, isNatural: boolean, apCost: number, isAtWill: boolean, read: LayerValueReader, contract: PlCostContract = plCostContract): DamageGpDetails {
    const dc = contract.damageCost;
    const dice = phpInt(readItemNumericSlot(read(arr, 'dice') ?? 1, 1));
    const die = phpInt(readItemNumericSlot(read(arr, 'die') ?? 8, 8));
    let avg = (dice > 0 && die > 0) ? dice * (die + 1) / 2.0 : 0;
    const mod = phpInt(readItemNumericSlot(read(arr, 'mod') ?? 0, 0));
    let modOnly = false;
    if (mod !== 0) {
        if (read(arr, 'dice') === null && read(arr, 'die') === null) { avg = 0; modOnly = true; }
        avg = Math.max(0, avg + mod);
    }
    let gp = phpIntRound(avg * dc.baseRateAttackRoll);
    const details: Record<string, unknown> = {
        dice: modOnly ? `${signed(mod)} flat` : `${dice}d${die}${mod !== 0 ? signed(mod) : ''}`,
        avg_damage: avg,
        gp_per_avg_point: dc.baseRateAttackRoll,
    };
    const pushFt = phpInt(readItemNumericSlot(read(arr, 'push_ft_after_hit') ?? 0, 0));
    if (pushFt > 0) {
        const pushGp = phpIntRound(pushFt * 20);
        gp += pushGp;
        details.push_ft = pushFt;
        details.push_gp = pushGp;
    }
    const damageRaw = read(arr, 'damage_type') ?? null;
    if (damageRaw !== null && damageRaw !== '') {
        const damageId = plCostLayerEntryUuid(damageRaw);
        if (damageId !== '') {
            const v = plCatalogs(contract).damageTypes.getVariableName(damageId) ?? 'bludgeoning';
            const mult = (Object.prototype.hasOwnProperty.call(dc.typeMultipliers, v) ? dc.typeMultipliers[v] : undefined) ?? 1.0;
            gp = phpIntRound(gp * mult);
            details.damage_type = v;
            details.type_multiplier = mult;
        }
    }
    if (isNatural) { gp = phpIntRound(gp * dc.naturalPremium); details.natural_premium = dc.naturalPremium; }
    if (apCost > 1) { gp = phpIntRound(gp * dc.extraApPenalty); details.extra_ap_penalty = dc.extraApPenalty; }
    if (isAtWill) { gp = phpIntRound(gp * 10); details.at_will_multiplier = 10; }
    return { gp: Math.max(10, gp), details };
}

export { phpRound, phpInt, phpIntRound };
export { statusQuantityInfo, plCatalogs, plCostContract, checkUuid } from './contract';
export { plCostLayerEntryUuid } from './layer-cells';
